/**
 * Projection registry & helpers for HEC-RAS Checker.
 *
 * Source of truth is the admin-managed catalog at /api/Projections/List.
 * Entries are cached in a mutable `PROJECTIONS` map keyed by slug. The WKT
 * text is fetched lazily and cached on first use so that subsequent
 * `findProjectionKeyByText` calls can match by exact text.
 */

import ApiService from "../services/ApiService";

// slug -> { key, label, name, cachedText? }
export const PROJECTIONS = {};

export const DEFAULT_PROJECTION = null; // user setting decides — no hard default

let listPromise = null;

const normalize = (text) => (text || '').toLowerCase().replace(/\s+/g, '');

const upsert = (entry) => {
    PROJECTIONS[entry.key] = {
        ...(PROJECTIONS[entry.key] || {}),
        ...entry,
    };
};

export const refreshProjections = async () => {
    listPromise = (async () => {
        const res = await ApiService.Projections.List();
        const items = Array.isArray(res?.data) ? res.data : (res?.data?.Data || []);
        // Wipe entries that no longer exist server-side.
        const existingKeys = new Set(items.map(i => i.name));
        Object.keys(PROJECTIONS).forEach(k => {
            if (!existingKeys.has(k)) delete PROJECTIONS[k];
        });
        items.forEach(i => upsert({
            key: i.name,
            label: i.display_name || i.name,
            name: i.display_name || i.name,
        }));
        return items.map(i => i.name);
    })();
    return listPromise;
};

export const ensureProjectionsLoaded = async () => {
    if (!listPromise) await refreshProjections();
    return listPromise;
};

export const getProjectionOptions = () => Object.entries(PROJECTIONS).map(([key, value]) => ({
    value: key,
    label: value.label,
}));

export const getProjection = (key) => PROJECTIONS[key] || null;

export const getProjectionName = (key) => PROJECTIONS[key]?.name || key;

export const getProjectionLabel = (key) => PROJECTIONS[key]?.label || key;

export const loadProjectionText = async (key) => {
    if (!key) return null;
    const cached = PROJECTIONS[key]?.cachedText;
    if (cached) return cached;
    const text = await ApiService.Projections.Text(key);
    if (text) upsert({key, cachedText: text});
    return text;
};

// Sync match against cached WKT texts only. Returns null if no cached
// projection text matches; callers that need full coverage should warm the
// cache with primeProjectionTexts() ahead of time.
export const findProjectionKeyByText = (wktText) => {
    const target = normalize(wktText);
    if (!target) return null;
    for (const [key, proj] of Object.entries(PROJECTIONS)) {
        if (proj?.cachedText && normalize(proj.cachedText) === target) return key;
    }
    return null;
};

// Pre-fetch all known projection texts so findProjectionKeyByText can match
// against them synchronously. Safe to call multiple times.
export const primeProjectionTexts = async () => {
    await ensureProjectionsLoaded();
    await Promise.all(Object.keys(PROJECTIONS).map(k => loadProjectionText(k)));
};

const ProjectionHelper = {
    PROJECTIONS,
    DEFAULT_PROJECTION,
    refreshProjections,
    ensureProjectionsLoaded,
    primeProjectionTexts,
    getProjectionOptions,
    getProjection,
    getProjectionName,
    getProjectionLabel,
    loadProjectionText,
    findProjectionKeyByText,
};

export default ProjectionHelper;
